/**
 * CashRegisterListController — page `/caisse.html`
 *
 * §7.J.C — Liste des sessions de caisse en deux zones :
 *   - Zone 1 « État des sessions en cours » (`openZone`) : rendue au-dessus
 *     de tout filtre, indépendante de la période. Affiche 1..N items via
 *     clone du template `#session-open-banner-template`, triés `opened_at ASC`.
 *   - Zone 2 « Historique des sessions clôturées » : tableau + filtres
 *     période + pagination + emptyState contextualisé (`#empty-state-template`)
 *     dont le message reflète les filtres actifs et expose un lien
 *     « Retirer les filtres » conditionnel.
 *   - GET /v1/cash-register/sessions retourne `open` + `closed` dans la
 *     même réponse ; on filtre côté client pour chaque zone.
 *   - 401 → toast + redirection /connexion.html (géré par api.js) ; 5xx → toast.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS —
 * uniquement clone de `<template>` + remplissage `data-slot`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const MONTH_LABELS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export default class CashRegisterListController extends BaseController {
  static targets = [
    "table",
    "openZone",
    "pagination",
    "emptyState",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
  };

  connect() {
    this._fetchSeq = 0;
    this._populateYearOptions();
    this._restoreFromQueryString();
    this._fetch();
  }

  /**
   * Peuple le filtre « Année » à partir de l'année courante (et les 5
   * précédentes). Évite toute année codée en dur dans le HTML — la plage
   * couvre largement l'historique réaliste d'un ME ; un filtre sur une année
   * sans session affiche simplement l'état vide.
   */
  _populateYearOptions() {
    const select = this.element.querySelector("select[data-action*='filterYear']");
    if (!select) return;
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= currentYear - 5; year--) {
      const option = document.createElement("option");
      option.value = String(year);
      option.textContent = String(year);
      select.appendChild(option);
    }
  }

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  filterYear() {
    this.pageValue = 1;
    this._fetch();
  }

  filterMonth() {
    this.pageValue = 1;
    this._fetch();
  }

  resetFilters() {
    for (const select of this.element.querySelectorAll("select[data-action*='cash-register-list']")) {
      select.value = "";
    }
    this.pageValue = 1;
    this._fetch();
  }

  clearFilters(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    this.resetFilters();
  }

  prevPage(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this.pageValue <= 1) return;
    this.pageValue = this.pageValue - 1;
    this._fetch();
  }

  nextPage(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    this.pageValue = this.pageValue + 1;
    this._fetch();
  }

  _currentFilters() {
    const filters = {};
    const yearSelect = this.element.querySelector("select[data-action*='filterYear']");
    if (yearSelect && yearSelect.value && /^\d{4}$/.test(yearSelect.value)) {
      filters.year = parseInt(yearSelect.value, 10);
    }
    const monthSelect = this.element.querySelector("select[data-action*='filterMonth']");
    if (monthSelect && monthSelect.value) {
      const monthIdx = MONTH_LABELS_FR.findIndex((m) => m === monthSelect.value);
      if (monthIdx >= 0) filters.month = monthIdx + 1;
      else if (/^\d{1,2}$/.test(monthSelect.value)) {
        filters.month = parseInt(monthSelect.value, 10);
      }
    }
    return filters;
  }

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("page")) {
      const p = parseInt(params.get("page"), 10);
      if (p > 0) this.pageValue = p;
    }
    const yearSelect = this.element.querySelector("select[data-action*='filterYear']");
    if (params.has("year") && yearSelect) yearSelect.value = params.get("year");
    const monthSelect = this.element.querySelector("select[data-action*='filterMonth']");
    if (params.has("month") && monthSelect) monthSelect.value = params.get("month");
  }

  _syncQueryString(filters) {
    const params = new URLSearchParams();
    if (filters.year) params.set("year", String(filters.year));
    if (filters.month) params.set("month", String(filters.month));
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async _fetch() {
    const seq = ++this._fetchSeq;
    const filters = this._currentFilters();
    this._syncQueryString(filters);

    try {
      const response = await api.cashRegister.listSessions({
        page: this.pageValue,
        perPage: this.perPageValue,
        year: filters.year ?? null,
        month: filters.month ?? null,
      });
      if (seq !== this._fetchSeq) return;
      const sessions = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? { page: this.pageValue, perPage: this.perPageValue, total: sessions.length };

      const openSessions = sessions
        .filter((s) => s.status === "open")
        .slice()
        .sort((a, b) => String(a.openedAt || "").localeCompare(String(b.openedAt || "")));
      this._renderOpenZone(openSessions);
      this._renderClosedRows(sessions.filter((s) => s.status === "closed"), filters);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les sessions de caisse.";
      this.showError(msg);
      this._renderClosedRows([], filters);
      this._renderOpenZone([]);
    }
  }

  _renderOpenZone(openSessions) {
    if (!this.hasOpenZoneTarget) return;
    this._clearChildren(this.openZoneTarget);

    if (!Array.isArray(openSessions) || openSessions.length === 0) {
      this.openZoneTarget.hidden = true;
      return;
    }

    for (const openSession of openSessions) {
      const fragment = this.cloneTemplate("session-open-banner-template");
      if (!fragment) continue;

      const movements = Array.isArray(openSession.manualMovements) ? openSession.manualMovements : [];
      const netMovements = movements.reduce((sum, m) => {
        const amount = Number(m.amount) || 0;
        return sum + (m.direction === "out" ? -amount : amount);
      }, 0);
      const individualCount = Array.isArray(openSession.individualTransactions)
        ? openSession.individualTransactions.length
        : 0;

      this.fillSlots(fragment, {
        "session-label": openSession.label || "Session sans libellé",
        "opened-at": this._formatDateTime(openSession.openedAt),
        "opening-balance": this._formatMoney(openSession.openingBalance) + " €",
        "net-movements": (netMovements >= 0 ? "+" : "") + this._formatMoney(netMovements) + " €",
        "individual-count": String(individualCount),
      });

      const sessionLink = fragment.querySelector('[data-slot="session-link"]');
      if (sessionLink) sessionLink.setAttribute("href", `/caisse-session.html?id=${encodeURIComponent(openSession.id)}`);

      // 99.19 — Clôture en bloc inline sur `caisse-session.html` (ancre
      // `#close-section`) ; la page dédiée `caisse-cloture.html` a été supprimée.
      const closeLink = fragment.querySelector('[data-slot="close-link"]');
      if (closeLink) closeLink.setAttribute("href", `/caisse-session.html?id=${encodeURIComponent(openSession.id)}#close-section`);

      this.openZoneTarget.appendChild(fragment);
    }

    this.openZoneTarget.hidden = false;
  }

  _renderClosedRows(sessions, filters = {}) {
    if (!this.hasTableTarget) return;
    this._clearChildren(this.tableTarget);

    if (sessions.length === 0) {
      this._renderEmptyState(filters);
      return;
    }
    if (this.hasEmptyStateTarget) {
      this._clearChildren(this.emptyStateTarget);
      this.emptyStateTarget.hidden = true;
    }

    for (const session of sessions) {
      const fragment = this.cloneTemplate("session-row-template");
      if (!fragment) continue;

      const txCount = Array.isArray(session.individualTransactions)
        ? session.individualTransactions.length
        : (session.transactionCount ?? 0);

      this.fillSlots(fragment, {
        "session-date": this._formatDate(session.sessionDate),
        "session-label": session.label || "—",
        "session-activity": session.activityLabel || (session.activityId ? "—" : "Toutes activités"),
        "opening-balance": this._formatMoney(session.openingBalance) + " €",
        "closing-balance": session.closingBalance != null
          ? this._formatMoney(session.closingBalance) + " €"
          : "—",
        "calculated-receipts": session.calculatedReceipts != null
          ? this._formatMoney(session.calculatedReceipts) + " €"
          : "—",
        "transaction-count": String(txCount),
      });

      const link = fragment.querySelector('[data-slot="session-link"]');
      if (link) link.setAttribute("href", `/caisse-session.html?id=${encodeURIComponent(session.id)}`);

      this.tableTarget.appendChild(fragment);
    }
  }

  _renderEmptyState(filters) {
    if (!this.hasEmptyStateTarget) return;
    this._clearChildren(this.emptyStateTarget);

    const fragment = this.cloneTemplate("empty-state-template");
    if (!fragment) {
      this.emptyStateTarget.hidden = false;
      return;
    }

    this.fillSlots(fragment, {
      "empty-message": this._emptyStateMessage(filters),
    });

    const hasFilter = Boolean(filters && (filters.year || filters.month));
    const linkWrap = fragment.querySelector('[data-slot-element="clearFiltersLink"]');
    if (linkWrap) linkWrap.hidden = !hasFilter;

    this.emptyStateTarget.appendChild(fragment);
    this.emptyStateTarget.hidden = false;
  }

  _emptyStateMessage(filters) {
    const year = filters && filters.year;
    const month = filters && filters.month;
    if (year && month) {
      const monthLabel = MONTH_LABELS_FR[month - 1] || String(month);
      return `Aucune session clôturée en ${monthLabel.toLowerCase()} ${year}.`;
    }
    if (year) {
      return `Aucune session clôturée en ${year}.`;
    }
    if (month) {
      const monthLabel = MONTH_LABELS_FR[month - 1] || String(month);
      return `Aucune session clôturée en ${monthLabel.toLowerCase()}.`;
    }
    return "Aucune session clôturée.";
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget) return;
    this._clearChildren(this.paginationTarget);

    const total = Number(pagination?.total ?? 0);
    const perPage = Number(pagination?.perPage ?? this.perPageValue) || this.perPageValue;
    const pageCount = Math.max(1, Math.ceil(total / perPage));
    if (pageCount <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }

    const fragment = this.cloneTemplate("pagination-template");
    if (!fragment) return;

    const sessionLabel = total > 1 ? "sessions clôturées" : "session clôturée";
    this.fillSlots(fragment, {
      "page-label": `Page ${this.pageValue} / ${pageCount} — ${total} ${sessionLabel}`,
      "current-page": String(this.pageValue),
    });

    const prevBtn = fragment.querySelector('[data-slot="prev-btn"]');
    if (prevBtn) prevBtn.disabled = this.pageValue <= 1;
    const nextBtn = fragment.querySelector('[data-slot="next-btn"]');
    if (nextBtn) nextBtn.disabled = this.pageValue >= pageCount;

    this.paginationTarget.appendChild(fragment);
    this.paginationTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _formatMoney(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "0,00";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  _formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
